import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';

import { LocalStorageService } from '../services/local-storage.service';
import { PathEnum } from '../shared/enums/path-enum';
import { deveSaltareProfiloCittadino } from '../shared/utils/ruoli-token.util';

/**
 * Guard di ruolo dell'area operatore. È la prima protezione PER RUOLO dell'app: fino a qui la
 * separazione fra cittadino e operatore era solo una scelta di instradamento post-login
 * (`OnboardingRouterService`), non un blocco d'accesso — chiunque poteva raggiungere l'area
 * dell'altro digitando l'URL.
 *
 * Il discrimine è `deveSaltareProfiloCittadino()`, lo stesso già usato da
 * `OnboardingRouterService` e `UserSessionService`: un utente con doppio ruolo
 * (cittadino + backoffice) vale come **cittadino**, così la sua esperienza non cambia.
 * Unico lettore dei ruoli resta `ruoli-token.util.ts`.
 *
 * <p><strong>Il backend resta autoritativo.</strong> Questo guard evita all'utente di finire in
 * un'area che non gli serve; non è la difesa del dato. L'endpoint di submit è chiuso a
 * SUPERADMIN | OP_COMUNE | OP_PRATICA con capability GESTIONE sul bando, e la lista è filtrata
 * lato server: un token manipolato qui non ottiene nulla.
 */
export const operatoreGuard: CanActivateFn & CanMatchFn = (): boolean | UrlTree => {
  const localStorage = inject(LocalStorageService);
  const router = inject(Router);

  if (!localStorage.getAccessToken()) {
    return router.createUrlTree([PathEnum.OPERATORE_LOGIN]);
  }

  if (deveSaltareProfiloCittadino(localStorage.decodeClaims()?.roles ?? [])) {
    return true;
  }

  // Autenticato ma non operatore: è un cittadino finito qui per errore (link condiviso,
  // cronologia). Va alla SUA area, non al login: ha già una sessione valida.
  return router.createUrlTree([PathEnum.PAGES + PathEnum.HOME]);
};

/**
 * Guard del login operatore: se c'è già una sessione operatore valida, salta la schermata di
 * accesso e porta direttamente alla lista bandi.
 *
 * È l'equivalente di `NoAuthGuard` per quest'area, ma non lo si può riusare: quello rimanda a
 * `/pages/home`, cioè proprio la shell del cittadino da cui l'operatore deve restare fuori.
 *
 * Un utente autenticato che NON è operatore passa: vedrà la schermata di accesso, con la via
 * d'uscita verso il login del cittadino. Meglio che rimbalzarlo altrove senza spiegazioni.
 */
export const operatoreLoggatoGuard: CanActivateFn & CanMatchFn = (): boolean | UrlTree => {
  const localStorage = inject(LocalStorageService);

  if (!localStorage.getAccessToken()) {
    return true;
  }

  if (deveSaltareProfiloCittadino(localStorage.decodeClaims()?.roles ?? [])) {
    return inject(Router).createUrlTree([PathEnum.OPERATORE_HOME]);
  }

  return true;
};

/**
 * Il complemento del guard sopra: tiene l'operatore FUORI dalla shell del cittadino.
 *
 * Serve perché la shell `/pages/*` non è neutra — monta il menu del cittadino e la discovery
 * mensa, che chiama endpoint `/cittadino/*` su cui un operatore prende 403. Prima che l'area
 * operatore fosse una rotta a sé, un operatore che finiva lì veniva espulso dall'interceptor 403
 * verso la home, e la home senza comune lo rimbalzava sull'onboarding: un ciclo infinito
 * (difformità B-01 del collaudo). Questo guard rimuove la causa invece del sintomo.
 *
 * Non blocca nessun cittadino: per lui `deveSaltareProfiloCittadino()` è falso e passa sempre.
 */
export const nonOperatoreGuard: CanActivateFn & CanMatchFn = (): boolean | UrlTree => {
  const localStorage = inject(LocalStorageService);

  // Nessun token: non è compito di questo guard: ci pensa `AuthGuard`, che sa anche
  // memorizzare l'URL di ritorno.
  if (!localStorage.getAccessToken()) {
    return true;
  }

  if (deveSaltareProfiloCittadino(localStorage.decodeClaims()?.roles ?? [])) {
    return inject(Router).createUrlTree([PathEnum.OPERATORE_HOME]);
  }

  return true;
};
